import { Link } from "react-router-dom";
import { useState } from "react";
import { ArrowUpRight, ArrowRight, CheckCircle2, Compass, Settings, Cpu, TrendingUp, Users, Building2, Landmark, Heart, GraduationCap, ChevronDown, Briefcase, Network, ShieldCheck } from "lucide-react";

// Sectors — pulled from the user's Wix Sectors page
const SECTORS = [
  {
    icon: Building2,
    label: "Corporations",
    sub: "Enterprise & mid-market",
    desc: "Retention pressure, outdated operating models, leadership inconsistency, AI adoption gaps, and execution drag.",
    happening: [
      "Growth without alignment",
      "Workforce shifts and retention pressure",
      "AI adoption without clear integration",
      "Strategy not translating into execution",
    ],
    approach:
      "Align leadership, operations, and AI-driven systems to improve execution, workforce performance, and scalability.",
    getValue:
      "Stronger leadership alignment, more efficient operations, and a workforce equipped to perform in a modern, AI-enabled environment.",
  },
  {
    icon: Landmark,
    label: "Government & Public Sector",
    sub: "Federal · State · Municipal",
    desc: "Modernization, siloed systems, workforce evolution, digital readiness, and operational accountability.",
    happening: [
      "Pressure to modernize systems and services",
      "Workforce gaps and evolving skill needs",
      "AI urgency with compliance and adoption challenges",
      "Increased demand with limited capacity",
    ],
    approach:
      "Design modernization strategies, workforce development programs, and AI integration approaches aligned with public sector realities.",
    getValue:
      "Improved service delivery, workforce readiness, and scalable systems that meet the needs of communities more effectively.",
  },
  {
    icon: Heart,
    label: "Nonprofit Ecosystems",
    sub: "Mission-driven organizations",
    desc: "Capacity strain, leadership pipeline gaps, funder readiness, operational structure, and sustainable execution.",
    happening: [
      "Multiple organizations operating in silos",
      "Funding tied to measurable outcomes",
      "Leadership burnout and capacity gaps",
      "Programs lacking structure or scalability",
    ],
    approach:
      "Strengthen ecosystem alignment, build capacity across organizations, and integrate AI and systems into program delivery.",
    getValue:
      "Stronger collaboration, increased capacity, sustainable leadership pipelines, and funder-aligned outcomes.",
  },
  {
    icon: GraduationCap,
    label: "Workforce Organizations",
    sub: "Cohort-based programs",
    desc: "Cohort delivery systems, employer alignment, participant retention, measurable outcomes, and scalable programming.",
    happening: [
      "Participant drop-off and low retention",
      "Training that doesn't translate into outcomes",
      "Lack of structure in cohort delivery",
      "Pressure to prove impact",
    ],
    approach:
      "Design cohort-based systems, integrate AI into training, and align programs with measurable workforce outcomes.",
    getValue:
      "Higher retention, stronger participant performance, and programs that deliver real, trackable results.",
  },
];

const PROCESS = [
  {
    n: "01",
    title: "Organizational Assessment",
    body: "Understand what's broken and why. We diagnose alignment across the five A.L.I.G.N.™ pillars.",
  },
  {
    n: "02",
    title: "Strategy & System Design",
    body: "Build the plan and the structure. Concrete operating models, not slideware.",
  },
  {
    n: "03",
    title: "Implementation & Training",
    body: "Equip leaders and teams to execute. We don't hand you a deck and disappear.",
  },
  {
    n: "04",
    title: "Advisory & Retainer",
    body: "Sustain and scale. Ongoing executive partnership as the work compounds.",
  },
];

// A.L.I.G.N. pillar tiles for the framework teaser block
const PILLARS = [
  { icon: Settings, label: "Operational", short: "A · Alignment" },
  { icon: Compass, label: "Leadership", short: "L · Leadership" },
  { icon: Cpu, label: "Technology & AI", short: "I · Intelligence" },
  { icon: TrendingUp, label: "Execution", short: "G · Governance" },
  { icon: Users, label: "Workforce", short: "N · Next" },
];

// Verbatim "If any of these sound familiar, we should talk." — from the live Wix homepage.
const FAMILIAR_QUESTIONS = [
  "Is your strategy clear but execution inconsistent?",
  "Are your teams working hard but not moving forward?",
  "Is AI on your radar but not in your operations?",
  "Are leadership decisions creating more confusion than clarity?",
  "Do your systems work independently but not together?",
];

// Capabilities — used by the scrolling capability ticker AND repeated in the
// firm-scale capabilities grid. Mirrors the live Wix capability strip.
const CAPABILITIES = [
  "Executive Alignment",
  "Workforce Readiness",
  "AI Integration",
  "Operational Performance",
  "Leadership Development",
  "Cohort-Based Training",
  "Modernization",
  "Retention Strategy",
];

export default function Home() {
  // Sector accordion — only one expanded at a time, collapsed by default
  const [openSector, setOpenSector] = useState(null);

  return (
    <main data-testid="page-home" className="pt-20">
      {/* ===================== HERO =====================
          Brand hero echoes the live Wix homepage:
          warm wood table, hands stacked over notebooks and laptops —
          the original Freedom In Me visual signature. */}
      <section className="relative overflow-hidden bg-[var(--navy-950)] text-[var(--bone)]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1573164574572-cb89e39749b4?auto=format&fit=crop&w=2000&q=80')",
            backgroundSize: "cover",
            backgroundPosition: "center 30%",
          }}
        />
        {/* Editorial overlay — lighter on the right so faces stay visible,
            keeps the executive copy legible on the left. */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(105deg, rgba(5,13,28,0.82) 0%, rgba(5,13,28,0.55) 40%, rgba(5,13,28,0.20) 78%, rgba(5,13,28,0.10) 100%)",
          }}
        />
        <div className="grain absolute inset-0 opacity-50"></div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-28 pb-24 lg:pt-40 lg:pb-36">
          <div className="max-w-4xl">
            <div className="eyebrow-light rise rise-1" style={{ color: "var(--gold-soft)" }}>
              Freedom In Me · Systems · Leadership · A.I.
            </div>
            <h1 className="font-display text-[44px] md:text-[64px] lg:text-[80px] leading-[1.02] mt-7 rise rise-2">
              Complexity Isn’t{" "}
              <span className="relative inline-block">
                <span style={{ color: "var(--gold-soft)" }}>Your Problem.</span>
                <span
                  className="absolute left-0 right-0 -bottom-1 h-[3px]"
                  style={{ background: "var(--gold-soft)" }}
                />
              </span>
              <br />
              <span className="whitespace-nowrap">Misalignment Is.</span>
            </h1>
            <p className="mt-10 max-w-2xl text-[18px] md:text-[20px] leading-[1.55] text-[var(--bone)]/90 rise rise-3">
              We help organizations, and the leaders guiding them, navigate workforce change,
              operational inefficiency, and AI modernization by aligning the systems that drive performance.
            </p>

            <div className="mt-12 flex flex-wrap items-center gap-4 rise rise-4">
              <Link
                to="/assessment"
                data-testid="hero-cta-assessment"
                className="px-7 py-4 text-[12px] tracking-[0.24em] uppercase font-semibold rounded-full inline-flex items-center gap-2 transition-all"
                style={{ background: "var(--gold)", color: "var(--bone)" }}
              >
                Take The Free 5-Min Diagnostic <ArrowRight size={16} />
              </Link>
              <Link
                to="/process"
                data-testid="hero-cta-framework"
                className="px-7 py-4 text-[12px] tracking-[0.24em] uppercase font-semibold rounded-full inline-flex items-center gap-2 border border-[var(--bone)]/40 text-[var(--bone)] hover:bg-[var(--bone)]/10 transition-colors"
              >
                How We Engage <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== CAPABILITIES MARQUEE =====================
          Infinite scrolling capability ticker — mirrors the live Wix
          "Executive Alignment · Workforce Readiness · …" capability strip. */}
      <section
        className="bg-[var(--navy-950)] border-y border-[var(--gold)]/15 py-5 overflow-hidden marquee-wrap"
        data-testid="section-capabilities-marquee"
        aria-label="Capabilities"
      >
        <div className="marquee-track">
          {[0, 1].map((dup) => (
            <div key={dup} className="inline-flex items-center gap-12 pr-12" aria-hidden={dup === 1}>
              {CAPABILITIES.map((c) => (
                <span key={`${dup}-${c}`} className="inline-flex items-center gap-12">
                  <span className="text-[13px] md:text-[15px] tracking-[0.22em] uppercase font-semibold text-[var(--bone)]">
                    {c}
                  </span>
                  <span className="text-[var(--gold-soft)]/60">·</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* ===================== PROVEN EXPERIENCE ===================== */}
      <section className="bg-[var(--bone)] py-16 lg:py-20 relative overflow-hidden" data-testid="section-proven">
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-4">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border" style={{ background: "rgba(31,107,72,0.08)", borderColor: "rgba(31,107,72,0.30)" }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--gold)" }}></span>
                <span className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: "var(--gold)" }}>
                  Proven Experience
                </span>
              </div>
              <h2 className="font-display text-[34px] md:text-[42px] leading-[1.08] text-[var(--navy-900)] mt-8">
                Built On The Seats<br />We Now Advise.
              </h2>
            </div>

            <div className="lg:col-span-8 grid md:grid-cols-3 gap-px bg-[var(--navy-900)]/10 self-start">
              {[
                {
                  k: "50+",
                  l: "Years of combined leadership",
                  sub: "Across public, private, and social sectors.",
                },
                {
                  k: "F25 · F500",
                  l: "Enterprise experience",
                  sub: "Spanning Fortune 25, Fortune 100, Fortune 500, federal agencies, and mission-driven organizations.",
                },
                {
                  k: "Network",
                  l: "Senior practitioner bench",
                  sub: "Organizational design, workforce development, AI strategy, and executive coaching.",
                },
              ].map((s) => (
                <div key={s.l} className="bg-[var(--bone)] p-7 lg:p-8">
                  <div className="font-display text-[32px] md:text-[40px] leading-none text-[var(--navy-900)]">
                    {s.k}
                  </div>
                  <div className="mt-4 text-[12px] tracking-[0.22em] uppercase text-[var(--navy-900)] font-semibold">
                    {s.l}
                  </div>
                  <div className="mt-3 text-[13px] leading-[1.6] text-[var(--muted)]">{s.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===================== SOUND FAMILIAR ===================== */}
      <section className="bg-[var(--bone)] py-24 lg:py-32" data-testid="section-sound-familiar">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="eyebrow">Our Capabilities</div>
            <h2 className="font-display text-[40px] md:text-[56px] leading-[1.04] text-[var(--navy-900)] mt-5">
              If Any Of These Sound Familiar,<br />
              We Should Talk.
            </h2>
          </div>

          <ul
            data-testid="sound-familiar-list"
            className="mt-16 grid md:grid-cols-2 gap-x-12 gap-y-6"
          >
            {FAMILIAR_QUESTIONS.map((q, i) => (
              <li
                key={q}
                data-testid={`sound-familiar-${i}`}
                className="flex items-start gap-4 group"
              >
                <span
                  className="mt-1 w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110"
                  style={{ background: "var(--gold)", color: "var(--bone)" }}
                  aria-hidden="true"
                >
                  <CheckCircle2 size={16} strokeWidth={2.25} />
                </span>
                <span className="text-[17px] md:text-[18px] leading-[1.55] text-[var(--navy-900)] font-medium pt-0.5">
                  {q}
                </span>
              </li>
            ))}
          </ul>

          <div className="mt-16 flex flex-wrap items-center gap-4">
            <Link
              to="/assessment"
              data-testid="sound-familiar-cta-assessment"
              className="btn-primary px-7 py-4 text-[12px] tracking-[0.24em] uppercase font-semibold rounded-full inline-flex items-center gap-2"
            >
              Diagnose your alignment <ArrowRight size={16} />
            </Link>
            <Link
              to="/contact"
              data-testid="sound-familiar-cta-contact"
              className="link-underline text-[13px] tracking-[0.22em] uppercase"
            >
              Book a strategy call <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ===================== SECTORS ===================== */}
      <section className="bg-[var(--cream)] py-24 lg:py-32 relative overflow-hidden" data-testid="section-sectors">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-16">
            <div className="lg:col-span-5">
              <div className="eyebrow">Who We Serve</div>
              <h2 className="font-display text-[40px] md:text-[52px] leading-[1.04] text-[var(--navy-900)] mt-5">
                Four Sectors.<br />
                One Operating Discipline.
              </h2>
              <p className="mt-6 text-[16px] leading-[1.7] text-[var(--muted)] max-w-md">
                Different missions, different mandates — but every organization we serve is under pressure to
                modernize without losing the people or the mission that got them here.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8" data-testid="sector-grid">
            {SECTORS.map((s, i) => {
              const Icon = s.icon;
              return (
                <article
                  key={s.label}
                  data-testid={`sector-card-${i}`}
                  className="bg-white border border-[var(--navy-900)]/8 p-8 lg:p-10 flex flex-col"
                >
                  {/* Header */}
                  <div className="flex items-start">
                    <div className="w-12 h-12 rounded-sm bg-[var(--navy-50)] flex items-center justify-center text-[var(--navy-900)]">
                      <Icon size={22} />
                    </div>
                  </div>

                  <h3 className="font-display text-[28px] md:text-[32px] leading-[1.1] text-[var(--navy-900)] mt-6">
                    {s.label}
                  </h3>
                  <div className="mt-2 text-[11px] tracking-[0.22em] uppercase text-[var(--muted)]">
                    {s.sub}
                  </div>
                  <p className="mt-5 text-[14px] leading-[1.65] text-[var(--muted)]">{s.desc}</p>

                  {/* WHAT IS HAPPENING */}
                  <div className="mt-10 hairline"></div>
                  <div className="mt-7">
                    <div className="text-[10px] tracking-[0.32em] uppercase text-[var(--gold)] font-bold">
                      What is happening?
                    </div>
                    <ul className="mt-5 space-y-3">
                      {s.happening.map((h) => (
                        <li
                          key={h}
                          className="flex items-start gap-3 text-[14px] leading-[1.6] text-[var(--navy-900)]"
                        >
                          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--gold)] flex-shrink-0"></span>
                          {h}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* OUR APPROACH */}
                  <div className="mt-10 hairline"></div>
                  <div className="mt-7">
                    <div className="text-[10px] tracking-[0.32em] uppercase text-[var(--gold)] font-bold">
                      Our approach
                    </div>
                    <p className="mt-5 text-[14px] leading-[1.7] text-[var(--navy-900)]">{s.approach}</p>
                  </div>

                  {/* WHAT YOU GET */}
                  <div className="mt-10 hairline"></div>
                  <div className="mt-7 flex-1">
                    <div className="text-[10px] tracking-[0.32em] uppercase text-[var(--gold)] font-bold">
                      What you get
                    </div>
                    <p className="mt-5 text-[14px] leading-[1.7] text-[var(--navy-900)]">{s.getValue}</p>
                  </div>

                  {/* CTA */}
                  <div className="mt-10">
                    <Link
                      to="/contact"
                      data-testid={`sector-cta-${i}`}
                      className="btn-primary inline-flex items-center gap-2 px-7 py-4 text-[12px] tracking-[0.22em] uppercase font-semibold rounded-full"
                    >
                      Explore {s.label} Solutions <ArrowRight size={16} />
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===================== A.L.I.G.N. TEASER ===================== */}
      <section className="bg-[var(--navy-950)] text-[var(--bone)] py-24 lg:py-32 relative overflow-hidden" data-testid="section-align-teaser">
        <div className="grain absolute inset-0"></div>
        <div
          className="absolute top-0 left-0 w-[500px] h-[500px] rounded-full opacity-50"
          style={{ background: "radial-gradient(circle, rgba(201,164,90,0.15), transparent 60%)" }}
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="eyebrow-light">Our Methodology</div>
              <h2 className="font-display text-[44px] md:text-[60px] leading-[1.02] mt-6">
                A.L.I.G.N.<span className="text-[var(--gold-soft)] align-super text-[20px] ml-1">™</span>
                <br />
                <span className="text-[var(--navy-100)]/70">The Systems That Decide Performance.</span>
              </h2>
              <p className="mt-8 max-w-xl text-[17px] leading-[1.6] text-[var(--navy-100)]/85">
                Five integrated systems — Operational, Leadership, Technology & AI, Execution, and
                Workforce — diagnosed, designed, and aligned around a single center of gravity:
                measurable execution.
              </p>

              <Link
                to="/align"
                data-testid="align-teaser-cta"
                className="mt-10 inline-flex items-center gap-2 text-[12px] tracking-[0.24em] uppercase font-semibold text-[var(--gold-soft)] hover:text-[var(--bone)] transition-colors"
              >
                See the full framework <ArrowUpRight size={16} />
              </Link>
            </div>

            <div className="lg:col-span-5 grid grid-cols-2 gap-3">
              {PILLARS.map((p, i) => {
                const Icon = p.icon;
                return (
                  <div
                    key={p.label}
                    data-testid={`pillar-tile-${i}`}
                    className={`p-5 border border-[var(--gold)]/20 bg-[var(--navy-900)]/40 backdrop-blur-sm transition-all duration-300 hover:bg-[var(--navy-900)]/70 hover:border-[var(--gold)]/50 ${
                      i === 4 ? "col-span-2" : ""
                    }`}
                  >
                    <Icon size={20} className="text-[var(--gold-soft)]" />
                    <div className="mt-3 text-[15px] font-semibold text-[var(--bone)]">{p.label}</div>
                    <div className="text-[11px] tracking-[0.18em] uppercase text-[var(--navy-100)]/60 mt-1">
                      {p.short}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ===================== PROCESS TEASER ===================== */}
      <section className="bg-[var(--bone)] py-24 lg:py-32" data-testid="section-process-teaser">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-14">
            <div className="lg:col-span-5">
              <div className="eyebrow">Our Process</div>
              <h2 className="font-display text-[40px] md:text-[52px] leading-[1.04] text-[var(--navy-900)] mt-5">
                A Deliberate Path<br />From Diagnosis To Durability.
              </h2>
            </div>
            <div className="lg:col-span-7 flex flex-col justify-end">
              <p className="text-[16px] leading-[1.7] text-[var(--muted)] max-w-xl">
                Four steps. One outcome. Every engagement is built around measurable alignment — not
                deliverables for their own sake.
              </p>
              <Link
                to="/process"
                data-testid="process-teaser-cta"
                className="mt-8 link-underline text-[13px] tracking-[0.22em] uppercase self-start"
              >
                See how we engage <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--navy-900)]/10">
            {PROCESS.map((p, i) => (
              <article
                key={p.n}
                data-testid={`process-step-${i}`}
                className="bg-[var(--bone)] p-8 lg:p-10 group hover:bg-white transition-colors"
              >
                <div className="font-display text-[64px] leading-none text-[var(--navy-900)]/15 group-hover:text-[var(--gold)]/60 transition-colors">
                  {p.n}
                </div>
                <h3 className="mt-6 font-display text-[22px] leading-[1.15] text-[var(--navy-900)]">
                  {p.title}
                </h3>
                <p className="mt-4 text-[14px] leading-[1.65] text-[var(--muted)]">{p.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== ECOSYSTEM / HUMAN IMPACT ===================== */}
      <section className="bg-[var(--cream)] py-24 lg:py-32 relative overflow-hidden" data-testid="section-ecosystem">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-5">
            <div className="eyebrow">Our Approach</div>
            <h2 className="font-display text-[40px] md:text-[52px] leading-[1.04] text-[var(--navy-900)] mt-5">
              Strategy With <span className="text-[var(--rose-deep)] italic">Intention</span>.<br />
              Systems With <span className="text-[var(--rose-deep)] italic">People</span>.
            </h2>
            <p className="mt-7 text-[16px] leading-[1.7] text-[var(--muted)] max-w-md">
              Most consulting firms optimize the workflow and leave you to figure out the human side.
              We do both — together — because alignment doesn’t hold in a system that doesn’t hold its people.
            </p>

            <ul className="mt-10 space-y-4">
              {[
                ["McKinsey rigor. Movement-building energy.", "Modern systems consulting, executed with humanity."],
                ["Built across sectors.", "Public, private, nonprofit, workforce — one operating discipline."],
                ["Ecosystems over engagements.", "We partner with the people who will still be there in year three."],
              ].map(([k, v]) => (
                <li key={k} className="grid grid-cols-[auto_1fr] gap-4">
                  <div className="w-2 h-2 mt-2 rounded-full bg-[var(--rose-deep)] flex-shrink-0" />
                  <div>
                    <div className="text-[15px] font-semibold text-[var(--navy-900)]">{k}</div>
                    <div className="text-[13px] leading-[1.55] text-[var(--muted)] mt-0.5">{v}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Photo collage — anchors the human-centered side of the brand AFTER the
              executive hero impression is set. Dominant image = warm, collaborative,
              diverse leadership conversation; supporting images lean strategic-planning. */}
          <div className="lg:col-span-7 grid grid-cols-6 grid-rows-6 gap-3 lg:gap-4 h-[520px] lg:h-[620px]">
            <div className="col-span-4 row-span-4 overflow-hidden bg-[var(--navy-900)] relative">
              <img
                src="https://images.unsplash.com/photo-1543269865-cbf427effbad?auto=format&fit=crop&w=1100&q=80"
                alt="Diverse leadership team in collaborative conversation"
                className="w-full h-full object-cover"
              />
              {/* Editorial caption pin — quietly upgrades the image from "stock"
                  to "this is the work" and reinforces firm-scale positioning. */}
              <div
                data-testid="collage-caption-pin"
                className="absolute bottom-4 left-4 right-4 sm:right-auto sm:max-w-[64%] bg-white border-l-[3px] px-4 py-3 shadow-[0_18px_40px_-22px_rgba(5,13,28,0.55)]"
                style={{ borderLeftColor: "var(--accent-gold)" }}
              >
                <div
                  className="text-[9px] tracking-[0.32em] uppercase font-bold"
                  style={{ color: "var(--gold)" }}
                >
                  Workforce Alignment Session
                </div>
                <div className="mt-1 text-[12px] leading-[1.45] text-[var(--navy-900)] font-medium">
                  Enterprise client engagement · Operations &amp; leadership
                </div>
              </div>
            </div>
            <div className="col-span-2 row-span-3 overflow-hidden bg-[var(--navy-900)] relative">
              <img
                src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=600&q=80"
                alt="Leadership team reviewing strategy documents"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="col-span-2 row-span-3 overflow-hidden bg-[var(--navy-900)] relative">
              <img
                src="https://images.unsplash.com/photo-1556761175-4b46a572b786?auto=format&fit=crop&w=600&q=80"
                alt="Executive working session in a modern workspace"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="col-span-3 row-span-2 overflow-hidden bg-[var(--cream)] relative">
              <img
                src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&w=700&q=80"
                alt="Strategic facilitation with notes mapped across a wall"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="col-span-3 row-span-2 overflow-hidden bg-[var(--navy-950)] text-[var(--bone)] flex items-end p-5">
              <div>
                <div className="text-[9px] tracking-[0.32em] uppercase font-bold" style={{ color: "var(--accent-gold-soft)" }}>
                  Where We Work
                </div>
                <p className="mt-2 font-display text-[18px] leading-[1.15] text-[var(--bone)]">
                  In the boardroom. At the table. Beside the leaders doing the work.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== WHAT BECOMES POSSIBLE ===================== */}
      <section className="bg-[var(--bone)] py-24 lg:py-32" data-testid="section-outcomes">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 mb-16">
            <div className="lg:col-span-7">
              <div className="eyebrow">Implementation Support</div>
              <h2 className="font-display text-[40px] md:text-[52px] leading-[1.04] text-[var(--navy-900)] mt-5">
                Transformation Isn’t A Slogan.<br />
                It’s A <span className="italic text-[var(--rose-deep)]">Measurable Shift</span> In How The Organization Operates.
              </h2>
            </div>
            <div className="lg:col-span-5 flex flex-col justify-end">
              <p className="text-[16px] leading-[1.7] text-[var(--muted)]">
                When organizations align — across leadership, operations, technology, execution, and workforce —
                the outcomes show up in the metrics that actually decide whether the strategy worked.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                area: "Leadership Effectiveness",
                outcome: "Decision cycles shorten. Accountability becomes visible. Leaders lead with confidence, not crisis.",
              },
              {
                area: "Workforce Readiness",
                outcome: "Talent stays. Capability deepens. Teams adopt new tools and roles without burning out the people inside them.",
              },
              {
                area: "Operational Execution",
                outcome: "Strategy turns into shipped work. Bottlenecks become routines. The org stops re-explaining the same priorities.",
              },
              {
                area: "AI Integration",
                outcome: "AI becomes infrastructure, not theater. Tools serve the work and free the people to do what only people can do.",
              },
            ].map((o) => (
              <article key={o.area} className="bg-white border border-[var(--navy-900)]/8 p-7 flex flex-col">
                <div className="text-[10px] tracking-[0.32em] uppercase text-[var(--rose-deep)] font-bold">
                  Outcome Area
                </div>
                <h3 className="font-display text-[22px] leading-[1.15] text-[var(--navy-900)] mt-3">
                  {o.area}
                </h3>
                <div className="mt-5 hairline"></div>
                <p className="mt-5 text-[14px] leading-[1.7] text-[var(--navy-900)]">{o.outcome}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== OUR TEAM ===================== */}
      <section className="bg-[var(--cream)] py-24 lg:py-32" data-testid="section-our-team">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-14">
            <div className="lg:col-span-5">
              <div className="eyebrow">Our Team</div>
              <h2 className="font-display text-[40px] md:text-[52px] leading-[1.04] text-[var(--navy-900)] mt-5">
                A Senior Practitioner Bench.<br />
                One Operating Discipline.
              </h2>
            </div>
            <div className="lg:col-span-7 flex flex-col justify-end">
              <p className="text-[16px] leading-[1.7] text-[var(--muted)] max-w-2xl">
                Freedom In Me is led by{" "}
                <Link to="/about" className="text-[var(--navy-900)] font-semibold underline underline-offset-2 hover:text-[var(--gold)]">
                  Donja Williams
                </Link>{" "}
                and supported by a senior practitioner network of operators, strategists, and ecosystem
                builders who have sat in the seats they now advise. Every engagement is staffed for the
                outcome — not the org chart.
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-6 lg:gap-8">
            {[
              {
                I: Briefcase,
                label: "Executive Operators",
                body: "Former COOs, regional GMs, and division heads who have run the systems we now redesign — Fortune 25 to mission-driven.",
              },
              {
                I: Network,
                label: "Cross-Functional Specialists",
                body: "Organizational design, workforce development, AI strategy, and executive coaching — assembled per engagement.",
              },
              {
                I: ShieldCheck,
                label: "Implementation Support",
                body: "Embedded leadership coaching, governance cadence, and AI integration support — so alignment lands and holds.",
              },
            ].map((t) => (
              <article
                key={t.label}
                data-testid={`team-card-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="bg-white border border-[var(--navy-900)]/8 p-7 lg:p-8 flex flex-col"
              >
                <div
                  className="w-12 h-12 rounded-sm flex items-center justify-center"
                  style={{ background: "rgba(31,107,72,0.08)", color: "var(--gold)" }}
                >
                  <t.I size={22} />
                </div>
                <div className="mt-6 text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: "var(--gold)" }}>
                  Capability
                </div>
                <h3 className="font-display text-[22px] leading-[1.15] text-[var(--navy-900)] mt-2">
                  {t.label}
                </h3>
                <div className="mt-5 hairline"></div>
                <p className="mt-5 text-[14px] leading-[1.7] text-[var(--navy-900)]">{t.body}</p>
              </article>
            ))}
          </div>

          <div className="mt-14 flex flex-wrap items-center gap-4">
            <Link
              to="/about"
              data-testid="team-cta-leadership"
              className="btn-primary px-7 py-4 text-[12px] tracking-[0.24em] uppercase font-semibold rounded-full inline-flex items-center gap-2"
            >
              Meet our leadership <ArrowRight size={16} />
            </Link>
            <Link
              to="/contact"
              data-testid="team-cta-contact"
              className="link-underline text-[13px] tracking-[0.22em] uppercase"
            >
              Build an engagement team <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ===================== LEADERSHIP EXPERIENCE =====================
          Reframes the previous "Trusted Across" trust strip into a precise
          founder-experience credibility band. The brand names below represent
          Donja Williams' professional leadership experience — they are NOT
          clients, partnerships, or endorsements of Freedom In Me, LLC. */}
      <section className="bg-[var(--navy-950)] text-[var(--bone)] py-16 lg:py-20 relative overflow-hidden" data-testid="section-leadership-experience">
        <div className="grain absolute inset-0 opacity-50"></div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <div className="eyebrow-light">Leadership Experience</div>
              <h3 className="font-display text-[28px] md:text-[34px] leading-[1.12] mt-5 text-[var(--bone)]">
                Earned Across Fortune 25,<br />
                Fortune 100, Fortune 500,<br />
                <span style={{ color: "var(--accent-gold-soft)" }}>Nonprofit & Community Ecosystems.</span>
              </h3>
              <p className="mt-6 max-w-md text-[14px] leading-[1.65] text-[var(--navy-100)]/70">
                Professional leadership and operational experience accumulated inside the
                organizations our work now serves — not as a vendor, but as the operator
                inside the seats we now advise.
              </p>
            </div>

            {/* Subtle, grayscale-styled organization wordmarks. Premium, restrained.
                Intentionally NOT a colorful "trusted-by" logo wall. */}
            <div className="lg:col-span-7">
              <div className="text-[9px] tracking-[0.32em] uppercase font-bold mb-6" style={{ color: "var(--accent-gold-soft)" }}>
                Operational Leadership Roles · A Selection
              </div>
              <ul
                data-testid="leadership-experience-list"
                className="grid grid-cols-2 md:grid-cols-3 gap-px bg-[var(--navy-100)]/8"
              >
                {[
                  "Verizon Wireless",
                  "AT&T",
                  "Frontier Communications",
                  "The Home Depot",
                  "Charter Communications",
                  "Spectrum",
                ].map((org) => (
                  <li
                    key={org}
                    className="bg-[var(--navy-950)] py-6 px-5 flex items-center justify-center text-center"
                  >
                    <span
                      className="font-display text-[18px] md:text-[20px] leading-tight tracking-tight"
                      style={{ color: "rgba(255,255,255,0.78)" }}
                    >
                      {org}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-[11px] leading-[1.6] text-[var(--navy-100)]/50 max-w-2xl">
                Organization names reflect Donja Williams' professional leadership and operational
                experience. Listed for credibility context only — not as clients, partners, or
                endorsements of Freedom In Me, LLC.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== CLOSING CTA ===================== */}
      <section className="bg-[var(--navy-950)] text-[var(--bone)] py-24 lg:py-32 relative overflow-hidden" data-testid="section-cta">
        <div className="grain absolute inset-0"></div>
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 text-center">
          {/* Free-vs-paid disambiguation pill — keeps the offer transparent up front */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border" style={{ background: "rgba(201,164,90,0.10)", borderColor: "rgba(201,164,90,0.45)" }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--accent-gold)" }}></span>
            <span className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: "var(--accent-gold-soft)" }}>
              Free · 5-Minute Diagnostic
            </span>
          </div>

          <h2 className="font-display text-[44px] md:text-[64px] leading-[1.02] mt-7">
            Start With The Readiness Index.<br />
            <span style={{ color: "var(--accent-gold-soft)" }}>End With Alignment That Holds.</span>
          </h2>

          <p className="mt-8 max-w-3xl mx-auto text-[17px] leading-[1.65] text-[var(--navy-100)]/85">
            The <strong className="text-[var(--bone)]">A.L.I.G.N.™ Readiness Index</strong> is a complimentary,
            five-minute diagnostic that returns an executive-grade snapshot of where your organization is
            aligned — and where it isn’t — across all five pillars. It is{" "}
            <span style={{ color: "var(--accent-gold-soft)" }} className="font-semibold">
              not the full Organizational Systems Assessment
            </span>
            . It is the first step: a high-level read that helps you and our team decide whether a deeper,
            paid engagement is the right next move.
          </p>

          {/* Two-track disambiguation: free tool vs. paid engagement */}
          <div className="mt-12 grid md:grid-cols-2 gap-px bg-[var(--accent-gold)]/30 max-w-4xl mx-auto text-left">
            <div className="bg-[var(--navy-950)] p-7 lg:p-8">
              <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: "var(--gold-soft)" }}>
                Step Zero · Free
              </div>
              <h3 className="font-display text-[22px] leading-[1.15] text-[var(--bone)] mt-3">
                A.L.I.G.N.™ Readiness Index
              </h3>
              <p className="mt-4 text-[13px] leading-[1.65] text-[var(--navy-100)]/75">
                Five-minute self-diagnostic. AI-generated executive snapshot. Confidential.
                Designed to help you understand at a high level where alignment is breaking down — before
                you commit to a deeper engagement.
              </p>
              <Link
                to="/assessment"
                data-testid="cta-take-readiness-index"
                className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full text-[11px] tracking-[0.24em] uppercase font-bold"
                style={{ background: "var(--gold)", color: "var(--bone)" }}
              >
                Take The Free Index <ArrowRight size={14} />
              </Link>
            </div>

            <div className="bg-[var(--navy-950)] p-7 lg:p-8 relative">
              <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: "var(--accent-gold-soft)" }}>
                Step One · Engagement
              </div>
              <h3 className="font-display text-[22px] leading-[1.15] text-[var(--bone)] mt-3">
                Organizational Systems Assessment
              </h3>
              <p className="mt-4 text-[13px] leading-[1.65] text-[var(--navy-100)]/75">
                The paid, in-depth diagnostic that opens every engagement: stakeholder interviews,
                document review, friction mapping, and an executive findings deck — scoped to your
                organization. This is where the actual work begins.
              </p>
              <Link
                to="/contact"
                data-testid="cta-book-call"
                className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full text-[11px] tracking-[0.24em] uppercase font-bold border"
                style={{ borderColor: "var(--accent-gold)", color: "var(--accent-gold-soft)" }}
              >
                Book A Strategy Call <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>

          <ul className="mt-14 flex flex-wrap justify-center gap-x-8 gap-y-3 text-[11px] tracking-[0.22em] uppercase text-[var(--navy-100)]/65">
            {[
              "5-Minute Completion",
              "Executive-Grade Report",
              "Confidential & No Obligation",
            ].map((x) => (
              <li key={x} className="inline-flex items-center gap-2">
                <CheckCircle2 size={14} style={{ color: "var(--accent-gold-soft)" }} /> {x}
              </li>
            ))}
          </ul>
        </div>
      </section>
    </main>
  );
}
